import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { CalendarPreference } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  useCalendarContext,
  useUpdateCalendarPref,
} from '../../../../../../data-access';
import { AppSwitch, CustomTooltip, CustomTooltipContent } from '../../../../../common';
import { ReactComponent as CalendarVisibilityIcon } from '../../../../../../assets/tabs/settings-tab/calendar-visibility-icon.svg';
import { ReactComponent as QuestionIcon } from '../../../../../../assets/tabs/settings-tab/question-icon.svg';
import CalendarVisibilityTooltipImage from '../../../../../../assets/onboarding/calendar-visiblity-tooltip.png';
import './CalendarVisibility.scss';

type HideAutoScheduleType = NonNullable<CalendarPreference['hideAutoSchedule']>;

const DEFAULT_HIDE_AUTO_SCHEDULE: HideAutoScheduleType = {
  completedTask: false,
  routine: false,
  exercise: false,
};

const CALENDAR_VISIBILITY_OPTIONS: {
  key: keyof HideAutoScheduleType;
  labelKey: string;
  tooltipKey?: string;
}[] = [
  { key: 'completedTask', labelKey: 'settings.nav.completedTasks' },
  { key: 'routine', labelKey: 'settings.nav.routine' },
  {
    key: 'exercise',
    labelKey: 'settings.nav.exercise',
    tooltipKey: 'settings.calendar.exerciseTooltip',
  },
];

const CalendarVisibility: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { updateCalendarPref } = useUpdateCalendarPref();
  const { calendarPreferences } = useCalendarContext();

  const [hideAutoSchedule, setHideAutoSchedule] = useState<HideAutoScheduleType>(
    DEFAULT_HIDE_AUTO_SCHEDULE
  );

  useEffect(() => {
    setHideAutoSchedule({
      ...DEFAULT_HIDE_AUTO_SCHEDULE,
      ...calendarPreferences.hideAutoSchedule,
    });
  }, [calendarPreferences.hideAutoSchedule]);

  const debouncedUpdateCalendarSettings = useMemo(
    () =>
      debounce((newData: Partial<CalendarPreference>) => {
        if (!user.preferenceId) {
          return;
        }

        return updateCalendarPref({
          preferenceId: user.preferenceId,
          preferences: { ...newData },
        });
      }, 1000),
    [updateCalendarPref, user.preferenceId]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateCalendarSettings.cancel();
    };
  }, [debouncedUpdateCalendarSettings]);

  const handleHideAutoScheduleChange = useCallback(
    (newValue: HideAutoScheduleType) => {
      setHideAutoSchedule(newValue);

      debouncedUpdateCalendarSettings({
        hideAutoSchedule: newValue,
      });
    },
    [debouncedUpdateCalendarSettings]
  );

  const handleOptionToggle = useCallback(
    (key: keyof HideAutoScheduleType, checked: boolean) => {
      handleHideAutoScheduleChange({
        ...hideAutoSchedule,
        [key]: !checked,
      });
    },
    [handleHideAutoScheduleChange, hideAutoSchedule]
  );

  return (
    <Stack
      id="calendarVisibility"
      gap={2}
      className="calendar-visibility"
      sx={{ scrollMarginTop: '12px' }}
    >
      <Stack gap={1} direction="row" alignItems="center">
        <CalendarVisibilityIcon />
        <Typography variant="body1" className="calendar-visibility__section-title">
          {t('settings.calendar.calendarVisibility')}
        </Typography>
        <CustomTooltip
          title={
            <CustomTooltipContent title={t('settings.calendar.calendarVisibility')} description="">
              <img
                src={CalendarVisibilityTooltipImage}
                alt={t('settings.calendar.calendarVisibilityPreviewAlt')}
                className="calendar-visibility__tooltip-image"
              />
            </CustomTooltipContent>
          }
          className="calendar-visibility__tooltip"
        >
          <QuestionIcon className="calendar-visibility__question-icon" />
        </CustomTooltip>
      </Stack>

      <Typography variant="subtitle2">
        {t('settings.calendar.calendarVisibilityDescription')}
      </Typography>

      <Stack className="calendar-visibility__list" gap={2}>
        {CALENDAR_VISIBILITY_OPTIONS.map((option, index) => (
          <Stack key={option.key} className="calendar-visibility__list-item-wrapper" gap={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className="calendar-visibility__list-item"
            >
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography variant="body1" className="calendar-visibility__list-item-title">
                  {t(option.labelKey)}
                </Typography>
                {option.tooltipKey && (
                  <CustomTooltip title={t(option.tooltipKey)}>
                    <QuestionIcon className="calendar-visibility__list-item-tooltip-icon" />
                  </CustomTooltip>
                )}
              </Stack>

              <AppSwitch
                checked={!hideAutoSchedule[option.key]}
                onChange={(event) => handleOptionToggle(option.key, event.target.checked)}
              />
            </Stack>

            {index !== CALENDAR_VISIBILITY_OPTIONS.length - 1 && (
              <Divider className="calendar-visibility__list-item-divider" />
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default CalendarVisibility;
